import React, { useState, useEffect } from 'react';
import './Schedule.css';
import { useTranslation } from "react-i18next";
import { apiFetch } from '../../helpers/api';
import Loader from '../common/Loader';
import { useQuery } from '@tanstack/react-query';

const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const Schedule = () => {
  const { t } = useTranslation();
  const [selectedLocation, setSelectedLocation] = useState("");

  // Fetch locations
  const {
    data: locations = [],
    isLoading: loadingLocations,
    isError: locationsError,
  } = useQuery({
    queryKey: ['locations'],
    queryFn: () => apiFetch('/api/locations'),
  });

const getAgeCategoryClass = (activity) => {
  const lower = activity.toLowerCase();

  if (lower.includes("4-6")) return "age-4-6";
  if (lower.includes("7-10") || lower.includes("7 - 10")) return "age-7-10";
  if (lower.includes("10-14") || lower.includes("10 - 14")) return "age-10-14";
  if (lower.includes("u10")) return "age-u10";
  if (lower.includes("u12") || lower.includes("u14")) return "age-u12-14";

  if (lower.includes("kumite") && lower.includes("senior")) return "age-kadet-junior-senior-kumite";
  if (lower.includes("kata") && lower.includes("senior")) return "age-kadet-junior-senior-kata";

  if (lower.includes("+16") || lower.includes("dorośli")) return "age-adult";

  return "age-default";
};

  // Automatically select last location if none is selected
// Automatically select default location if none is selected
useEffect(() => {
  if (!selectedLocation && locations.length > 0) {
    // Try to find "Głowne Dojo"
    const dojo = locations.find((loc) =>
      loc.name.toLowerCase().includes("główne dojo")
    );

    if (dojo) {
      setSelectedLocation(dojo.id);
    } else {
      // fallback: select last location
      setSelectedLocation(locations[locations.length - 1].id);
    }
  }
}, [locations, selectedLocation]);

  // Fetch schedule for the selected location
  const {
    data: schedule = [],
    isLoading: loadingSchedule,
    isError: scheduleError,
  } = useQuery({
    queryKey: ['schedule', selectedLocation],
    queryFn: () => apiFetch(`/api/schedule?locationId=${selectedLocation}`),
    enabled: !!selectedLocation,
    select: (data) => {
      const allHoursSet = new Set();
      const grouped = {};

      data.forEach(({ day, time, activity }) => {
        allHoursSet.add(time);
        if (!grouped[time]) grouped[time] = {};
        if (!grouped[time][day]) grouped[time][day] = [];
        grouped[time][day].push(activity);
      });

      const sortedTimes = Array.from(allHoursSet).sort();

      return sortedTimes.map(time => ({
        time,
        days: daysOfWeek.reduce((acc, day) => {
          acc[day] = grouped[time]?.[day] || [];
          return acc;
        }, {})
      }));
    }
  });

  // Loading state
  if (loadingLocations || loadingSchedule) {
    return <div><Loader /></div>;
  }

  // Error state
  if (locationsError || scheduleError) {
    return <div className="error-msg">❌ {t("error.loadingFailed", "Failed to load schedule")}</div>;
  }

  return (
    <div className="schedule-container">
      <h2>🥋 {t("schedule.title")}</h2>

      <div className="dropdown mb-4 schedule-location-dropdown">
        <button
          className="btn btn-secondary dropdown-toggle"
          type="button"
          data-bs-toggle="dropdown"
          data-bs-display="static"
          aria-expanded="false"
        >
          {locations.find(loc => loc.id === selectedLocation)?.name || "Select Location"}
        </button>
        <ul className="dropdown-menu schedule-location-menu">
          {locations.map((loc) => (
            <li key={loc.id}>
              <button
                className="dropdown-item"
                onClick={() => setSelectedLocation(loc.id)}
              >
                {loc.name}
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="schedule-grid-timealigned">
        <div className="schedule-row header">
          <div className="schedule-time-header">🕒</div>
          {daysOfWeek.map(day => (
            <div key={day} className="schedule-day-header">
              {t(`schedule.days.${day.toLowerCase()}`)}
            </div>
          ))}
        </div>

        {schedule.map(({ time, days }) => (
          <div className="schedule-row" key={time}>
            <div className="schedule-time-label">{time}</div>
            {daysOfWeek.map(day => (
              <div className="schedule-cell" key={day}>
                {days[day].length > 0 ? (
                  days[day].map((activity, i) => (
                    <div key={i} className={`schedule-event ${getAgeCategoryClass(activity)}`}>
                      {activity}
                    </div>
                  ))
                ) : (
                  <div className="schedule-empty">&nbsp;</div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Schedule;
